import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useId } from "react";
import { createPortal } from "react-dom";
import "./ReviewActivity.scss";

const ActivityContext = createContext(null);
export function useActivityFeed() { return Boolean(useContext(ActivityContext)); }
export function ReviewActivityProvider({ children }) {
  const [records, setRecords] = useState({});
  const [hosts, setHosts] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const register = useCallback((id, record) => {
    setRecords(current => ({ ...current, [id]: record }));
    return () => setRecords(current => { const next = { ...current }; delete next[id]; return next; });
  }, []);
  const host = useCallback((id, element) => setHosts(current => {
    if (current[id] === element) return current;
    const next = { ...current }; if (element) next[id] = element; else delete next[id]; return next;
  }), []);
  const value = useMemo(() => ({ records, hosts, register, host, expandedId, setExpandedId }), [records, hosts, register, host, expandedId]);
  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

// Outside the question workspace these records retain their original rendering.
export function ActivityRecord({ id, type, createdOn, children, listItem = false, title = "", author = "", status = "", expanded, onToggle }) {
  const context = useContext(ActivityContext);
  const register = context?.register;
  const setExpandedId = context?.setExpandedId;
  useEffect(() => {
    if (!setExpandedId || expanded === undefined) return;
    setExpandedId(current => expanded ? id : current === id ? null : current);
  }, [expanded, id, setExpandedId]);
  useEffect(() => register?.(id, { type, createdOn, listItem, title, author, status, expanded, onToggle }), [register, id, type, createdOn, listItem, title, author, status, expanded, onToggle]);
  if (!context) return children;
  return context.hosts[id] ? createPortal(children, context.hosts[id]) : null;
}
function ActivitySlot({ id, record, hidden }) {
  const { host, expandedId, setExpandedId } = useContext(ActivityContext);
  const expanded = expandedId === id;
  const bodyId = useId();
  const toggle = () => {
    setExpandedId(expanded ? null : id);
    if (!expanded && record.expanded === false) record.onToggle?.();
  };
  const ref = useCallback(element => host(id, element), [host, id]);
  return <div hidden={hidden} className={`review-activity__record is-${record.type.toLowerCase()}`}>
    <span className="review-activity__icon" aria-hidden="true"><i className={`bx ${ { Evidence: "bx-paperclip", Query: "bx-message-rounded-dots", Comment: "bx-comment", Observation: "bx-note", Finding: "bx-error" }[record.type] || "bx-note"}`} /></span>
    <button type="button" className="review-activity__toggle" aria-expanded={expanded} aria-controls={bodyId} onClick={toggle}>
      <span className="review-activity__summary"><span className="review-activity__labels"><span className="review-activity__type">{record.type}</span>{record.status && <span className="question-status">{record.status}</span>}</span>{record.title && <strong>{record.title}</strong>}{record.author && <small>Added by {record.author}</small>}</span>
      {record.createdOn && <time dateTime={record.createdOn}>{new Date(record.createdOn).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</time>}
      <i className={`bx bx-chevron-${expanded ? "up" : "down"}`} aria-hidden="true" />
    </button>
    {record.listItem ? <ul id={bodyId} hidden={!expanded} className={record.type === "Query" ? "checklist-review__question-record-list" : "checklist-review__simple-records"} ref={ref} /> : <div id={bodyId} hidden={!expanded} className={record.type === "Evidence" ? "checklist-review__evidence-references" : "checklist-review__discussion-workspace"} ref={ref} />}
  </div>;
}
export default function ReviewActivity({ children, actions }) {
  const { records } = useContext(ActivityContext);
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("newest");
  const entries = Object.entries(records).sort(([aId, a], [bId, b]) => {
    const date = value => { const result = Date.parse(value); return Number.isFinite(result) ? result : 0; };
    return (sort === "newest" ? -1 : 1) * (date(a.createdOn) - date(b.createdOn)) || aId.localeCompare(bId);
  });
  const count = entries.filter(([, record]) => filter === "All" || filter === record.type).length;
  return <section className="review-activity" aria-label="Review activity">
    <header className="review-activity__heading"><h2>Review Activity <small>{entries.length} items</small></h2>
      <div><label><span className="visually-hidden">Activity type</span><select aria-label="Activity type" value={filter} onChange={event => setFilter(event.target.value)}>{["All", "Evidence", "Query", "Comment", "Observation", "Finding"].map(type => <option key={type} value={type}>{type === "All" ? "All activity" : type}</option>)}</select></label>
      <select aria-label="Sort review activity" value={sort} onChange={event => setSort(event.target.value)}><option value="newest">Newest first</option><option value="oldest">Oldest first</option></select></div>
    </header>
    <div className="review-activity__actions">{actions}</div>
    <div className="review-activity__sources">{children}</div>
    <div className="review-activity__feed">{entries.map(([id, record]) => <ActivitySlot key={id} id={id} record={record} hidden={filter !== "All" && filter !== record.type} />)}</div>
    {!count && <p className="question-record-empty">{entries.length ? "No activity matches this filter." : "No activity has been added for this question."}</p>}
  </section>;
}
